/**
 * PDF 페이지 래스터라이저 — pdfjs-dist 로 문서 페이지를 PNG object URL 로 만든다.
 *
 * - 문서·페이지 단위 Promise 캐시라 같은 페이지는 한 번만 래스터한다(프리뷰 재생 중
 *   매 프레임 재계산 없음).
 * - 워커는 번들 asset URL 로 로드(webpack/turbopack `new URL` 자산). 프리뷰·렌더
 *   서비스(Remotion webpack 번들) 양쪽에서 동일하게 동작한다.
 * - ponytail: 래스터 배율은 고정 2x(스케일 캐시 폭발 방지). 줌 키프레임 고배율에서
 *   선명도가 부족해지면 (url, page, scale-step) 캐시로 확장.
 */
import QuickLRU from '../../utils/quick-lru';

const RASTER_SCALE = 2;

/** 페이지 PNG object URL 캐시 상한 — evict 시 revoke 로 메모리 회수. */
const PAGE_URL_CACHE_MAX = 60;

/** 0-based pageIndex 를 [0, numPages-1] 로 클램프. */
export const clampPageIndex = (pageIndex: number, numPages: number): number =>
  Math.min(Math.max(0, pageIndex), Math.max(0, numPages - 1));

/**
 * 페이지를 컴포지션에 contain 배치했을 때의 아이템 크기.
 * 치수가 비정상(0/NaN)이면 컴포지션 크기로 폴백한다.
 */
export const fitPageInComposition = (
  pageWidth: number,
  pageHeight: number,
  compositionWidth: number,
  compositionHeight: number,
): { width: number; height: number } => {
  const scale = Math.min(
    compositionWidth / pageWidth,
    compositionHeight / pageHeight,
  );
  const width = Math.round(pageWidth * scale);
  const height = Math.round(pageHeight * scale);
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width < 1 ||
    height < 1
  ) {
    return { width: compositionWidth, height: compositionHeight };
  }
  return { width, height };
};

type PdfjsModule = typeof import('pdfjs-dist');

let pdfjsModulePromise: Promise<PdfjsModule> | null = null;

const getPdfjs = (): Promise<PdfjsModule> => {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString();
      return pdfjs;
    });
  }
  return pdfjsModulePromise;
};

const documentCache = new Map<
  string,
  Promise<import('pdfjs-dist').PDFDocumentProxy>
>();

const getDocumentCached = (url: string) => {
  let cached = documentCache.get(url);
  if (!cached) {
    cached = getPdfjs().then((pdfjs) => pdfjs.getDocument(url).promise);
    cached.catch(() => documentCache.delete(url));
    documentCache.set(url, cached);
  }
  return cached;
};

export interface PdfDocumentMeta {
  numPages: number;
  /** 1페이지 pt 단위 치수 (scale 1 viewport) */
  pageWidth: number;
  pageHeight: number;
}

/** 업로드/아이템 생성 시 문서 메타 파악용. */
export const getPdfDocumentMeta = async (
  url: string,
): Promise<PdfDocumentMeta> => {
  const doc = await getDocumentCached(url);
  const page = await doc.getPage(1);
  const viewport = page.getViewport({ scale: 1 });
  return {
    numPages: doc.numPages,
    pageWidth: viewport.width,
    pageHeight: viewport.height,
  };
};

// LRU — evict 시 revokeObjectURL 로 blob 메모리를 회수한다(무한 성장 방지).
const pageUrlCache = new QuickLRU<string, Promise<string>>({
  maxSize: PAGE_URL_CACHE_MAX,
  onEviction: (_key, value) => {
    value.then((objectUrl) => URL.revokeObjectURL(objectUrl)).catch(() => {});
  },
});

/**
 * 페이지 PNG object URL. pageIndex 는 0-based. 실패 Promise 는 캐시에서 제거해
 * 재시도 가능하게 둔다.
 */
export const renderPdfPageToUrl = (
  url: string,
  pageIndex: number,
): Promise<string> => {
  const cacheKey = `${url}#${pageIndex}`;
  let cached = pageUrlCache.get(cacheKey);
  if (!cached) {
    cached = (async () => {
      const doc = await getDocumentCached(url);
      const page = await doc.getPage(
        clampPageIndex(pageIndex, doc.numPages) + 1,
      );
      const viewport = page.getViewport({ scale: RASTER_SCALE });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('PDF 렌더용 canvas 2d context 를 만들 수 없습니다');
      }
      await page.render({ canvasContext: context, viewport }).promise;
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('PDF 페이지 toBlob 실패'))),
          'image/png',
        );
      });
      return URL.createObjectURL(blob);
    })();
    cached.catch(() => pageUrlCache.delete(cacheKey));
    pageUrlCache.set(cacheKey, cached);
  }
  return cached;
};
